import { Caption1, Tab, TabList, Tooltip, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { Document16Regular, DocumentMultiple16Regular, LockClosed12Regular, Note16Regular, People16Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ConfirmDialog } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton, FuiFilterSelect } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { ListSearch, ListToolbar } from "@/components/list-toolbar";
import type { DocumentKind } from "@/engine/types";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { AssigneeAvatars } from "@/features/tasks/task-ui";
import { formatDate } from "@/lib/date-format";
import { contentShell, listBody, shellGutter } from "@/shell/content-frame";
import { usePageAction } from "@/shell/page-action";
import { planHref } from "@/shell/project-nav";
import { useProjectStore } from "@/store/project-store";
import { DocKindIcon, DocLinkMark, DocLinkMore } from "./DocLinkChip";
import { DocFilterMenu, RowMenu } from "./DocList";
import { DOCUMENT_LINK_PREVIEW, presentLink } from "./doc-links";
import { openDocumentTarget } from "./doc-open";
import {
  filterWorkspaceDocuments,
  sortWorkspaceDocuments,
  workspaceDocumentScale,
  type WorkspaceDocRow,
  type WorkspaceDocScope,
} from "./doc-index";
import {
  DOCUMENT_KIND_LABEL,
  DOCUMENT_VISIBILITY_LABEL,
  documentCode,
  documentTitle,
  parseDocLinkFilter,
  parseDocSection,
  type DocSection,
  type DocSortKey,
} from "./doc-model";
import { linkedExecutionIds } from "./doc-reader";
import { canDeleteDocument, canMakePrivate } from "./doc-visibility";
import { setWorkspaceDocumentVisibility, trashWorkspaceDocument, useWorkspaceDocRows } from "./use-workspace-docs";

const KIND_EMPTY: Record<DocumentKind, { title: string; description: string }> = {
  meeting: { title: "회의록이 없습니다", description: "정한 내용은 작업과 이슈로 연결됩니다" },
  memo: { title: "메모가 없습니다", description: "다듬기 전의 글을 여기에 둡니다" },
  material: { title: "문서가 없습니다", description: "붙여 넣은 글은 여기서 고칩니다" },
};

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  body: {
    ...listBody,
    padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}`,
    gap: tokens.spacingVerticalS,
  },
  planFilter: { width: "180px" },
  row: {
    cursor: "pointer",
    ":hover td": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active td": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  codeCell: {
    width: "1%",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground2,
  },
  codeInner: {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  titleCell: {
    maxWidth: 0,
    width: "100%",
    minWidth: "240px",
  },
  titleLine: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
    maxWidth: "100%",
  },
  lockIcon: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  shrink: {
    width: "1%",
    whiteSpace: "nowrap",
  },
  planCell: {
    maxWidth: "160px",
  },
  links: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "4px",
    maxWidth: "360px",
  },
  muted: { color: tokens.colorNeutralForeground3 },
});

function docPreviewLine(doc: WorkspaceDocRow["doc"], readableContent: boolean): string {
  if (!readableContent) return "";
  const first =
    doc.plainText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find((line) => line.length > 0) ?? "";
  if (doc.kind === "meeting" && doc.heldAt) {
    const held = `회의 ${formatDate(doc.heldAt, "numeric")}`;
    return first ? `${held} · ${first}` : held;
  }
  return first;
}

/** 워크스페이스에서 읽을 수 있는 문서. 본문은 그 플랜의 편집기로 연다. */
export function WorkspaceDocList() {
  const styles = useStyles();
  const table = useDataTableStyles();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const library = useWorkspaceDocRows();
  const [cursor, setCursor] = useState(0);
  const [searchRequest, setSearchRequest] = useState(0);
  const [pendingTrash, setPendingTrash] = useState<WorkspaceDocRow | null>(null);
  const [pendingPrivate, setPendingPrivate] = useState<WorkspaceDocRow | null>(null);

  const rawScope = params.get("scope");
  const scope: WorkspaceDocScope = rawScope === "plan" || rawScope === "private" ? rawScope : "all";
  const mine = params.get("mine") === "1";
  const section = parseDocSection(params.get("view"));
  const query = params.get("q") ?? "";
  const rawSort = params.get("sort");
  const sort: DocSortKey = rawSort === "title" || rawSort === "seq" ? rawSort : "updated";
  const linkFilter = parseDocLinkFilter(params.get("link"));
  const planId = params.get("plan") ?? "";

  const patch = (next: Record<string, string | null>) => {
    const updated = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === null || value === "") updated.delete(key);
      else updated.set(key, value);
    }
    setParams(updated, { replace: true });
  };

  const actorId = library[0]?.reader.actorId ?? "";
  const description = useMemo(() => workspaceDocumentScale(library), [library]);
  const visible = useMemo(
    () =>
      sortWorkspaceDocuments(
        filterWorkspaceDocuments(library, {
          text: query,
          section,
          scope,
          mine,
          link: linkFilter,
          planId,
          actorId,
          contentReadable: (row) => {
            const listed = library.find((item) => item.key === row.key);
            return listed?.contentReadable ?? false;
          },
        }),
        sort,
      ),
    [library, query, section, scope, mine, linkFilter, planId, actorId, sort],
  );

  const planChoices = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of library) map.set(row.projectId, row.projectName);
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "ko"));
  }, [library]);
  const showPlanFilter = planChoices.length > 1;
  const showPlan = showPlanFilter && !planId;
  const patchRef = useRef(patch);
  patchRef.current = patch;
  useEffect(() => {
    if (!planId || planChoices.length === 0) return;
    if (!planChoices.some((plan) => plan.id === planId)) patchRef.current({ plan: null });
  }, [planId, planChoices]);

  useEffect(() => {
    setCursor((index) => Math.min(index, Math.max(visible.length - 1, 0)));
  }, [visible.length]);

  usePageAction(null);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) return;
      event.preventDefault();
      setSearchRequest((count) => count + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const selectSection = (next: DocSection) => {
    if (next === section) return;
    patch({ view: next === "all" ? null : next, q: null });
  };

  const open = (row: WorkspaceDocRow) => {
    useProjectStore.getState().openProject(row.projectId);
    navigate(planHref(row.projectId, `docs/${row.doc.id}`));
  };

  const onRowKey = (event: KeyboardEvent<HTMLTableRowElement>, index: number, row: WorkspaceDocRow) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const next = Math.min(index + 1, visible.length - 1);
      setCursor(next);
      (event.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const next = Math.max(index - 1, 0);
      setCursor(next);
      (event.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      open(row);
    }
  };

  const narrowed = query.trim().length > 0 || scope !== "all" || mine || linkFilter !== "all" || !!planId;
  const showVisibility = scope === "all";
  const kindEmpty = section === "all" ? null : KIND_EMPTY[section];

  return (
    <div className={styles.page}>
      <ListToolbar
        title="문서"
        description={description}
        sections={
          <TabList
            size="small"
            selectedValue={section}
            aria-label="문서"
            onTabSelect={(_, data) => {
              const next = data.value;
              if (next === "all" || next === "meeting" || next === "memo" || next === "material") selectSection(next);
            }}
          >
            <Tab value="all" icon={<DocumentMultiple16Regular />}>전체</Tab>
            <Tab value="material" icon={<Document16Regular />}>문서</Tab>
            <Tab value="meeting" icon={<People16Regular />}>회의록</Tab>
            <Tab value="memo" icon={<Note16Regular />}>메모</Tab>
          </TabList>
        }
        search={<ListSearch value={query} requestOpen={searchRequest} onChange={(value) => patch({ q: value || null })} />}
        filters={
          <>
            <SegmentedControl
              value={scope}
              ariaLabel="공개 범위"
              onChange={(value) => patch({ scope: value === "all" ? null : value })}
              options={[
                { value: "all", label: "전체" },
                { value: "plan", label: "공개" },
                { value: "private", label: "비공개" },
              ]}
            />
            {showPlanFilter ? (
              <FuiFilterSelect
                className={styles.planFilter}
                label="플랜"
                ariaLabel="플랜"
                value={planChoices.some((plan) => plan.id === planId) ? planId : "all"}
                options={[{ value: "all", label: "모든 플랜" }, ...planChoices.map((plan) => ({ value: plan.id, label: plan.name }))]}
                onValueChange={(value) => patch({ plan: value === "all" ? null : value })}
              />
            ) : null}
            <DocFilterMenu
              mine={mine}
              link={linkFilter}
              sort={sort}
              onAuthor={(value) => patch({ mine: value === "mine" ? "1" : null })}
              onLink={(value) => patch({ link: value === "none" ? "none" : null })}
              onSort={(value) => patch({ sort: value === "updated" ? null : value })}
              onReset={() => patch({ mine: null, link: null })}
            />
          </>
        }
      />
      <div className={styles.body}>
        {visible.length === 0 ? (
          library.length === 0 ? (
            <GhostEmptyState title="이 워크스페이스에 문서가 없습니다" description="문서는 플랜에서 추가합니다." />
          ) : !narrowed && kindEmpty ? (
            <GhostEmptyState title={kindEmpty.title} description={kindEmpty.description} />
          ) : (
            <GhostEmptyState variant="search" title="일치하는 문서가 없습니다" description="" />
          )
        ) : (
          <div className={table.frame}>
            <table className={table.table} aria-label="문서">
              <thead>
                <tr>
                  <th className={table.th}>번호</th>
                  <th className={table.th}>제목</th>
                  <th className={table.th}>종류</th>
                  {showVisibility ? <th className={table.th}>구분</th> : null}
                  <th className={table.th}>작성자</th>
                  {showPlan ? <th className={table.th}>플랜</th> : null}
                  <th className={table.th}>연결</th>
                  <th className={mergeClasses(table.th, table.num)}>수정일</th>
                  <th className={table.th}>동작</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row, index) => {
                  const doc = row.doc;
                  const title = documentTitle(doc.title);
                  const linked = linkedExecutionIds(doc.id, row.links);
                  const canDelete = canDeleteDocument(doc, row.reader, linked);
                  const canPrivate = canMakePrivate(doc, row.reader);
                  const preview = docPreviewLine(doc, row.contentReadable);
                  return (
                    <tr
                      key={row.key}
                      className={styles.row}
                      tabIndex={index === cursor ? 0 : -1}
                      onClick={() => open(row)}
                      onKeyDown={(event) => onRowKey(event, index, row)}
                      onFocus={() => setCursor(index)}
                    >
                      <td className={mergeClasses(table.td, styles.codeCell)}>
                        <span className={styles.codeInner}>
                          <DocKindIcon kind={doc.kind} />
                          <span>{documentCode(doc.seq)}</span>
                        </span>
                      </td>
                      <td className={mergeClasses(table.td, styles.titleCell)}>
                        <Tooltip content={preview ? `${title}\n${preview}` : title} relationship="description">
                          <span className={table.twoLine}>
                            <span className={styles.titleLine}>
                              <span className={table.clip}>{title}</span>
                              {showVisibility && doc.visibility === "private" ? <LockClosed12Regular className={styles.lockIcon} /> : null}
                            </span>
                            {preview ? <Caption1 className={table.role}>{preview}</Caption1> : null}
                          </span>
                        </Tooltip>
                      </td>
                      <td className={mergeClasses(table.td, styles.shrink)}>{DOCUMENT_KIND_LABEL[doc.kind]}</td>
                      {showVisibility ? (
                        <td className={mergeClasses(table.td, styles.shrink, doc.visibility === "private" && styles.muted)}>
                          {DOCUMENT_VISIBILITY_LABEL[doc.visibility]}
                        </td>
                      ) : null}
                      <td className={mergeClasses(table.td, styles.shrink)} title={doc.authorId || "작성자 미정"}>
                        {doc.authorId ? <AssigneeAvatars names={[doc.authorId]} /> : <span className={styles.muted}>—</span>}
                      </td>
                      {showPlan ? (
                        <td className={mergeClasses(table.td, styles.planCell)}>
                          <Tooltip content={row.projectName} relationship="description">
                            <span className={table.clip}>{row.projectName}</span>
                          </Tooltip>
                        </td>
                      ) : null}
                      <td
                        className={mergeClasses(table.td, row.links.length === 0 && styles.muted)}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <LinkMarks row={row} />
                      </td>
                      <td className={mergeClasses(table.td, table.num, styles.muted)} title={formatDate(doc.updatedAt, "dateTime")}>
                        {formatDate(doc.updatedAt, "numeric")}
                      </td>
                      <td
                        className={mergeClasses(table.td, styles.shrink)}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <RowMenu
                          canTrash={canDelete}
                          canPrivate={doc.visibility === "plan" && canPrivate}
                          canShare={doc.visibility === "private" && canDelete}
                          canRestore={false}
                          canPurge={false}
                          onTrash={() => setPendingTrash(row)}
                          onPrivate={() => setPendingPrivate(row)}
                          onShare={() => setWorkspaceDocumentVisibility(row.projectId, doc.id, "plan")}
                          onRestore={() => {}}
                          onPurge={() => {}}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={!!pendingTrash}
        title="문서를 휴지통으로 옮길까요"
        message="이 문서를 휴지통으로 옮깁니다. 연결된 작업과 이슈는 남고, 그쪽 목록에서는 이 문서가 잠시 숨습니다."
        confirmLabel="휴지통으로"
        onCancel={() => setPendingTrash(null)}
        onConfirm={() => {
          if (pendingTrash) trashWorkspaceDocument(pendingTrash.projectId, pendingTrash.doc.id);
          setPendingTrash(null);
        }}
      />
      <ConfirmDialog
        open={!!pendingPrivate}
        title="비공개로 바꿀까요?"
        message="다른 멤버의 목록과 연결 칩에서 이 문서가 사라집니다. 작업과 이슈는 그대로입니다."
        confirmLabel="비공개로 전환"
        onCancel={() => setPendingPrivate(null)}
        onConfirm={() => {
          if (pendingPrivate) setWorkspaceDocumentVisibility(pendingPrivate.projectId, pendingPrivate.doc.id, "private");
          setPendingPrivate(null);
        }}
      />
    </div>
  );
}

function LinkMarks({ row }: { row: WorkspaceDocRow }) {
  const styles = useStyles();
  if (!row.links.length) return <span>—</span>;
  const shown = row.links.slice(0, DOCUMENT_LINK_PREVIEW);
  const rest = row.links.slice(DOCUMENT_LINK_PREVIEW);
  const restTitles = rest
    .map((link) => {
      const chip = presentLink(link, row.catalog);
      const label = [chip.code, chip.title].filter(Boolean).join(" ");
      return chip.missing ? `${label} · 삭제됨` : label;
    })
    .join("\n");
  const openLink = (kind: Parameters<typeof openDocumentTarget>[0], id: string) => {
    const store = useProjectStore.getState();
    if (store.activeId !== row.projectId) store.openProject(row.projectId);
    openDocumentTarget(kind, id);
  };
  return (
    <span className={styles.links}>
      {shown.map((link) => {
        const chip = presentLink(link, row.catalog);
        return <DocLinkMark key={link.id} chip={chip} onOpen={chip.missing ? undefined : () => openLink(chip.targetKind, chip.targetId)} />;
      })}
      <DocLinkMore count={rest.length} titles={restTitles} />
    </span>
  );
}
